import { createContext, useContext, useState, useMemo, type ReactNode } from 'react';

export type ComponentEditDraft = {
  componentId: string;
  data: Record<string, unknown>;
} | null;

type ComponentEditDraftContextValue = {
  draft: ComponentEditDraft;
  setDraft: (draft: ComponentEditDraft) => void;
};

const ComponentEditDraftContext = createContext<ComponentEditDraftContextValue | null>(null);

export function ComponentEditDraftProvider({ children }: { children: ReactNode }) {
  const [draft, setDraft] = useState<ComponentEditDraft>(null);
  const value = useMemo(() => ({ draft, setDraft }), [draft]);
  return (
    <ComponentEditDraftContext.Provider value={value}>
      {children}
    </ComponentEditDraftContext.Provider>
  );
}

export function useComponentEditDraft() {
  return useContext(ComponentEditDraftContext);
}

/** Merge in-progress edit modal form data over the saved component payload. */
export function mergeComponentEditDraft<T extends Record<string, unknown>>(
  componentId: string,
  savedData: T,
  draft: ComponentEditDraft
): T {
  if (draft?.componentId === componentId && draft.data && typeof draft.data === 'object') {
    return { ...savedData, ...draft.data } as T;
  }
  return savedData;
}

/** Apply the open edit-modal draft to a page's component list (e.g. responsive preview). */
export function applyComponentEditDraftToComponents<T extends { id: string; data: unknown }>(
  components: T[],
  draft: ComponentEditDraft
): T[] {
  if (!draft) return components;
  return components.map(c => {
    if (c.id !== draft.componentId) return c;
    return {
      ...c,
      data: mergeComponentEditDraft(c.id, (c.data ?? {}) as Record<string, unknown>, draft),
    };
  });
}
